import { requireById } from './dom'
import { hideTip, showToast } from './admin-ui'
import { initInbox } from './admin-inbox'
import { initBookings } from './admin-bookings'
import { initSocial } from './admin-social'
import { initEngagement } from './admin-engagement'
import { initPerformance } from './admin-performance'

const VIEW_ORDER = ['inbox', 'bookings', 'social', 'engagement', 'performance'] as const

type ViewName = (typeof VIEW_ORDER)[number]

/** Scroll each pinned view holds, in viewport-heights. */
const TRACK_PER_VIEW = 1.05

/** Matches the admin.css breakpoint below which the app doesn't pin. */
const PINNED_QUERY = '(min-width: 901px)'

/**
 * The operator-app frame. On desktop it pins mid-viewport while the scroll
 * position steps through the three live views, then releases; sidebar clicks
 * scroll the page to the matching segment so scroll and tab always agree.
 * On small screens (and for locked tabs) it behaves as plain tabs. Each
 * view's behaviour lives in its own module.
 */
export function initAdminApp(): void {
  const sidebar = requireById('asnav')
  const track = requireById('apptrack')
  const app = requireById('appwrap')
  const pinned = window.matchMedia(PINNED_QUERY)

  let current: ViewName = 'inbox'

  // Swaps are instant — the entry animation in admin.css marks the change.
  // Anything slower and a view can be scrolled past before it ever shows.
  function switchView(view: ViewName): void {
    if (view === current) return
    current = view

    sidebar.querySelectorAll<HTMLElement>('.as-item').forEach((b) => {
      b.classList.toggle('on', b.dataset['view'] === view)
    })

    document.querySelectorAll<HTMLElement>('.app-view').forEach((v) => v.classList.remove('on'))
    requireById(`view-${view}`).classList.add('on')
    hideTip()
  }

  /* --- scroll-driven views --- */

  /** The app's pinned offset from the viewport top (its CSS sticky `top`). */
  const stickyTop = (): number => parseFloat(getComputedStyle(app).top) || 0

  /** The scroll range the pin lasts for, in px. */
  const pinRange = (): number => track.offsetHeight - app.offsetHeight

  function sizeTrack(): void {
    track.style.height = pinned.matches
      ? `${Math.round(app.offsetHeight + VIEW_ORDER.length * TRACK_PER_VIEW * window.innerHeight)}px`
      : ''
  }

  function onScroll(): void {
    if (!pinned.matches) return
    const range = pinRange()
    if (range <= 0) return
    const gone = stickyTop() - track.getBoundingClientRect().top
    const progress = Math.min(0.999, Math.max(0, gone / range))
    switchView(VIEW_ORDER[Math.floor(progress * VIEW_ORDER.length)]!)
  }

  /** Scroll to the middle of a view's segment; onScroll then flips the tab. */
  function scrollToView(view: ViewName): void {
    const pinStart = track.getBoundingClientRect().top + window.scrollY - stickyTop()
    const segment = ((VIEW_ORDER.indexOf(view) + 0.5) / VIEW_ORDER.length) * pinRange()
    window.scrollTo({ top: Math.round(pinStart + segment), behavior: 'smooth' })
  }

  sidebar.addEventListener('click', (event) => {
    const item = (event.target as HTMLElement).closest<HTMLButtonElement>('.as-item')
    if (!item) return

    if (item.dataset['locked'] !== undefined) {
      showToast('That tab ships with the product — five tabs are live in this demo.')
      return
    }

    const view = item.dataset['view'] as ViewName | undefined
    if (!view || view === current) return

    // While pinned, the page's scroll position and the open tab must agree —
    // so the click scrolls, and the scroll switches the tab.
    if (pinned.matches) scrollToView(view)
    else switchView(view)
  })

  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', () => {
    sizeTrack()
    onScroll()
  })
  sizeTrack()

  initInbox()
  initBookings()
  initSocial()
  initEngagement()
  initPerformance()
}
